import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api';
import { MOCK_ITEM_DEFS, wornSlot } from '../api/itemCatalog';
import { ApiError, type Pack, type PackItem, type TrajeDto } from '../api/types';
import { t, numero } from '../i18n/idioma';
import { nomeDoItem, nomeDoSlot } from '../i18n/nomes';
import { iconeDaPrata, iconeDoOuro } from '../assets/items';
import { overlayDoPack } from '../world/spriteAtlas';
import { ItemIcon } from './ItemIcon';
import { carteiraDoPack, formatGold, itemHint, stones } from './itemUi';
import { DRAG_CORPSE, DRAG_ITEM } from './LootGump';
import { useFechar } from '../input/useAtalhos';
import { usePrenderFoco } from '../input/usePrenderFoco';
import { comoDataTransfer, prepararArraste, useAlvoDeArraste, useAlvoNoDocumento, type Soltura } from '../input/arraste';
import { Gump } from './Gump';
import { IcGuardaRoupa } from './icones/IconesV3';
import { COR_DA_RARIDADE, corDaTinta, skinDoItem } from './guardaRoupa';
import {
  DEAD_LOCK, ICON, ORDEM_VISUAL, PACK_SLOTS, SLOT_POR_ID, faixaDaDurabilidade, faixaDoPeso,
  focoDepoisDeJogar, fracao, lerCorpseSlot, mergeBagIds, mesmaPilha, placeAt, separavel, verboDoEncaixe
} from './inventario';
import { useQuantas } from './Quantas';

export { DRAG_ITEM };

interface Props {
  active: boolean;
  tick: number;
  onChanged(text: string): void;
  /** `qty`: quantas da pilha (o "Quantas?" já perguntou). */
  onOffer?(item: PackItem, qty: number): void;
  offeredIds?: readonly string[];
  onMount?(defId: string | null): void;
  onCloth?(defId: string | null): void;
  onWeapon?(defId: string | null): void;
  onShield?(defId: string | null): void;
  dead?: boolean;
  onCorpseDrop?(id: number, itemId: string, qty?: number): void;
  onWallet?(gold: number, silver: number): void;
  onBankDeposit?(item: PackItem, qty: number): void;
  /** Abre o menu de vendas (techspec-vendas). */
  onVender?(): void;
  /** Abre o guarda-roupa (techspec-guardaroupa). */
  onGuardaRoupa?(): void;
  /** Canvas do boneco. Vem do WorldScreen, como o do perfil: o motor lê a ref na montagem. */
  boneco?: ReactNode;
}

function dropForaDaMochila(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof Element)) return true;
  return !alvo.closest('.pack, .win--inventory, .win--banco, .win--shop, .trade, .modal');
}

interface FichaProps {
  item: PackItem;
  /** Encaixe do gump (`mainHand`, `helm`…) — `null` para o que não se veste. */
  slot: string | null;
  travado: boolean;
  busy: boolean;
  onVestir(): void;
  onJogar(): void;
  /** Pilha com mais de 1: separa N numa célula nova. */
  onSeparar?(): void;
  /** Tinta prime: guardar no guarda-roupa. `guardada` vem do guarda-roupa (null enquanto não sabe). */
  tinta?: { guardada: boolean | null; raridade?: string | null; skin?: boolean; onGuardar(): void };
}

function FichaDoItem({ item, slot, travado, busy, onVestir, onJogar, onSeparar, tinta }: FichaProps) {
  const dur = faixaDaDurabilidade(item.dur, item.durMax);
  const nome = nomeDoItem(item.defId, item.name);
  const durTxt = dur ? t('mochila.durabilidade', { a: item.dur!, b: item.durMax! }) : '';
  const selo = tinta?.raridade
    ? { cls: ` gr-rar-${tinta.raridade}`, chave: `traje.raridade.${tinta.raridade}` }
    : item.qualidade === 2
    ? { cls: ' is-exc', chave: 'oficina.excepcional' }
    : item.qualidade === 1
      ? { cls: ' is-mao', chave: 'mochila.craftado' }
      : { cls: '', chave: 'item.comum' };
  return (
    <div className="inv-ficha">
      <span className="inv-ficha__ico" aria-hidden="true"><ItemIcon id={item.defId} size={ICON} quantity={item.qty} /></span>
      <p className="ficha__nome" title={nome}>{nome}</p>
      <div className="ficha__meta">
        <span className={`rar${selo.cls}`}>{t(selo.chave)}</span>
        {slot && <span>{nomeDoSlot(SLOT_POR_ID.get(slot)!.chave)}</span>}
        <span className="mono">{stones(item.weight)} {t('comum.stones')}</span>
        {dur && (
          <span className={`dur is-${dur}`} title={durTxt}>
            <span className="dur__trilho" aria-hidden="true"><span className="dur__cheio" style={{ width: `${fracao(item.dur!, item.durMax!)}%` }} /></span>
            <span aria-hidden="true">{item.dur}/{item.durMax}</span>
            <span className="sr">{durTxt}</span>
          </span>
        )}
      </div>
      {item.feitoPor && <p className="ficha__marca">{t('oficina.feitoPor')} <b>{item.feitoPor}</b></p>}
      {tinta && <p className="ficha__txt">{t(tinta.skin
        ? (tinta.guardada ? 'traje.skin.fichaJaGuardada' : 'traje.skin.fichaGuardar')
        : (tinta.guardada ? 'traje.fichaJaGuardada' : 'traje.fichaGuardar'))}</p>}
      <div className="ficha__acoes">
        {tinta && (
          <button type="button" className="btn btn--tiny btn--primary" disabled={busy || tinta.guardada !== false}
            onClick={tinta.onGuardar}>
            {t(tinta.guardada ? 'traje.jaGuardada' : 'traje.guardar')}
          </button>
        )}
        {slot && (
          <button type="button" className="btn btn--tiny" disabled={busy || travado}
            title={travado ? t('mochila.travadoMorto') : undefined} onClick={onVestir}>
            {t(verboDoEncaixe(slot))}
          </button>
        )}
        {onSeparar && (
          <button type="button" className="btn btn--tiny" disabled={busy} onClick={onSeparar}>
            {t('mochila.separar')}
          </button>
        )}
        {!item.abencoado && (
        <button type="button" className="btn btn--tiny btn--ghost btn--perigo" disabled={busy} onClick={onJogar}>
          {t('mochila.dropConfirmar')}
        </button>
        )}
      </div>
    </div>
  );
}

export function InventoryGump({ active, tick, onChanged, onOffer, offeredIds = [], onMount, onCloth, onWeapon, onShield, dead = false, onCorpseDrop, onWallet, onBankDeposit, onVender, onGuardaRoupa, boneco }: Props) {
  const [pack, setPack] = useState<Pack | null>(null);
  /** O guarda-roupa, só quando há Tinta prime na mochila: diz se a cor já está guardada e a raridade. */
  const [traje, setTraje] = useState<TrajeDto | null>(null);
  const [defs] = useState(() => MOCK_ITEM_DEFS);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bagIds, setBagIds] = useState<(string | null)[]>([]);
  const [overBag, setOverBag] = useState<number | null>(null);
  const [alvoDoArraste, setAlvoDoArraste] = useState<string | null>(null);
  const raizRef = useRef<HTMLDivElement>(null);
  const packRef = useRef(pack);
  packRef.current = pack;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const offerRef = useRef(onOffer);
  offerRef.current = onOffer;
  useEffect(() => {
    if (onOffer) setSelected(null);
  }, [onOffer]);
  const [dropAlvo, setDropAlvo] = useState<PackItem | null>(null);
  const quantas = useQuantas();
  const slotRefs = useRef(new Map<string, HTMLButtonElement>());
  const hitRefs = useRef(new Map<number, HTMLButtonElement>());
  const focoDepois = useRef<{ tipo: 'slot'; id: string } | { tipo: 'cell'; i: number } | null>(null);
  const origemModal = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  usePrenderFoco(modalRef, dropAlvo !== null, '[data-foco-inicial]');

  /** Não lê estado: o listener de janela guarda a versão da primeira renderização. */
  const pedirDrop = (item: PackItem) => {
    if (item.abencoado) { setError(t('mochila.blessed')); return; }
    setError(null);
    const ativo = document.activeElement;
    origemModal.current = ativo instanceof HTMLElement && ativo !== document.body ? ativo : null;
    setDropAlvo(item);
  };

  const fecharModal = () => {
    setDropAlvo(null);
    const volta = origemModal.current; origemModal.current = null;
    if (volta?.isConnected) requestAnimationFrame(() => volta.focus());
  };

  useEffect(() => {
    if (!active) return;
    let live = true;
    Promise.all([api.getPack()])
      .then(([next]) => {
        if (!live) return;
        setPack(next);
        setError(null);
        const carteira = carteiraDoPack(next);
        onWallet?.(carteira.gold, carteira.silver);
        onMount?.(dead ? null : (next.items.find(i => i.equippedSlot === 'mount')?.defId ?? null));
        onCloth?.(dead ? null : ((next.cloth ?? overlayDoPack(next.items)) || null));
        onWeapon?.(dead ? null : (next.items.find(i => i.equippedSlot === 'mainHand')?.defId ?? null));
        onShield?.(dead ? null : (next.items.find(i => i.equippedSlot === 'offHand')?.defId ?? null));
      })
      .catch(err => {
        if (live) setError(err instanceof ApiError ? err.message : t('mochila.naoAbriu'));
      });
    return () => { live = false; };
  }, [active, tick, dead]);

  const temTinta = !!pack?.items.some(i => !i.equippedSlot && (corDaTinta(i.defId) !== null || skinDoItem(i.defId) !== null));
  useEffect(() => {
    if (!active || !temTinta) return;
    let live = true;
    api.getTraje().then(d => { if (live) setTraje(d); }).catch(() => { /* a ficha fica sem saber; o servidor confere */ });
    return () => { live = false; };
  }, [active, temTinta, tick]);

  const guardarTinta = async (item: PackItem) => {
    if (busyRef.current) return;
    setBusy(true);
    try {
      // A skin vai pelo mesmo gesto, noutro endpoint (techspec-skins §06).
      const d = skinDoItem(item.defId) ? await api.guardarSkin(item.id) : await api.guardarTinta(item.id);
      setTraje(d);
      setSelected(null);
      onChanged(t(skinDoItem(item.defId) ? 'traje.skin.guardou' : 'traje.guardou', { item: nomeDoItem(item.defId, item.name) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  const bySlot = useMemo(() => {
    const map = new Map<string, PackItem>();
    for (const item of pack?.items ?? []) {
      if (item.equippedSlot) map.set(item.equippedSlot, item);
    }
    return map;
  }, [pack]);

  const loose = useMemo(
    () => (pack?.items ?? []).filter(i => !i.equippedSlot),
    [pack]
  );

  useEffect(() => {
    setBagIds(prev => mergeBagIds(prev, loose.map(i => i.id)));
  }, [loose]);

  useFechar(() => fecharModal(), { ativo: dropAlvo !== null, modal: true });

  // Soltar fora da mochila (e das janelas de banco, loja, troca) joga o item no chão.
  useAlvoNoDocumento({
    sobre: ({ carga, alvo }) => {
      const id = carga[DRAG_ITEM];
      return !!id && !busyRef.current && !offerRef.current && dropForaDaMochila(alvo)
        && !!packRef.current?.items.some(i => i.id === id);
    },
    solta: ({ carga }) => {
      setAlvoDoArraste(null);
      const item = packRef.current?.items.find(i => i.id === carga[DRAG_ITEM]);
      if (item) pedirDrop(item);
    }
  }, active && !onOffer);

  // Peça igual é pilha de verdade no servidor (techspec-pilha-de-pecas): cada linha é uma célula, com o número.
  const cells = useMemo(() => {
    const byId = new Map(loose.map(i => [i.id, i]));
    const n = Math.max(PACK_SLOTS, bagIds.length);
    return Array.from({ length: n }, (_, i) => {
      const id = bagIds[i];
      return id ? (byId.get(id) ?? null) : null;
    });
  }, [loose, bagIds]);

  useEffect(() => { setAlvoDoArraste(null); }, [pack]);

  useEffect(() => {
    const f = focoDepois.current; focoDepois.current = null;
    if (!f) return;
    requestAnimationFrame(() => (f.tipo === 'slot' ? slotRefs.current.get(f.id) : hitRefs.current.get(f.i))?.focus());
  }, [pack]);

  const apply = (next: Pack) => {
    setPack(next);
    setSelected(null);
    const carteira = carteiraDoPack(next);
    onWallet?.(carteira.gold, carteira.silver);
    onMount?.(dead ? null : (next.items.find(i => i.equippedSlot === 'mount')?.defId ?? null));
    onCloth?.(dead ? null : ((next.cloth ?? overlayDoPack(next.items)) || null));
    onWeapon?.(dead ? null : (next.items.find(i => i.equippedSlot === 'mainHand')?.defId ?? null));
    onShield?.(dead ? null : (next.items.find(i => i.equippedSlot === 'offHand')?.defId ?? null));
  };

  const equip = async (item: PackItem, focoEm?: string) => {
    const slot = wornSlot(defs.get(item.defId)?.equipSlot ?? '');
    if (dead && slot && DEAD_LOCK.has(slot)) {
      setError(slot === 'mount' ? t('mochila.deadMount') : t('mochila.deadCloth'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.equipItem(item.id);
      if (focoEm) focoDepois.current = { tipo: 'slot', id: focoEm };
      apply(next);
      onChanged(slot === 'mount'
        ? t('mochila.voceMonta')
        : t('mochila.voceVeste', { item: nomeDoItem(item.defId, item.name) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('mochila.naoVestiu'));
    } finally {
      setBusy(false);
    }
  };

  const unequip = async (item: PackItem, bagIndex?: number) => {
    setBusy(true);
    setError(null);
    try {
      const wasMount = item.equippedSlot === 'mount';
      apply(await api.unequipItem(item.id));
      if (bagIndex !== undefined) setBagIds(prev => placeAt(prev, item.id, bagIndex));
      onChanged(wasMount
        ? t('mochila.voceDesmontou')
        : t('mochila.voceTira', { item: nomeDoItem(item.defId, item.name) }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('mochila.naoTirou'));
    } finally {
      setBusy(false);
    }
  };

  const confirmarDrop = async () => {
    const alvo = dropAlvo;
    if (!alvo || busy) return;
    setBusy(true);
    setError(null);
    try {
      const i = cells.findIndex(c => c?.id === alvo.id);
      const next = await api.dropItem(alvo.id);
      const k = i >= 0 ? focoDepoisDeJogar(cells.map((c, j) => (j === i ? null : c)), i) : null;
      focoDepois.current = k !== null
        ? { tipo: 'cell', i: k }
        : { tipo: 'slot', id: ORDEM_VISUAL.find(id => !(dead && DEAD_LOCK.has(id))) ?? 'mainHand' };
      apply(next);
      origemModal.current = null;
      onChanged(t('mochila.voceJoga', { item: nomeDoItem(alvo.defId, alvo.name) }));
      setDropAlvo(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('mochila.naoJogou'));
    } finally {
      setBusy(false);
    }
  };

  /** Separa `qty` da pilha numa linha nova; com `celula`, a nova vai para essa célula (techspec-pilha-de-pecas §13). */
  const separar = async (item: PackItem, qty: number, celula?: number) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.separarItem(item.id, qty);
      apply(r.pack);
      const soltos = r.pack.items.filter(i => !i.equippedSlot).map(i => i.id);
      setBagIds(prev => {
        const base = mergeBagIds(prev, soltos);
        return celula === undefined ? base : placeAt(base, r.novoId, celula);
      });
      setSelected(r.novoId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  /** Junta a pilha `item` na `destino`, da mesma chave. O que passar do teto fica na origem. */
  const juntar = async (item: PackItem, destino: PackItem) => {
    setBusy(true);
    setError(null);
    try {
      apply(await api.juntarItem(item.id, destino.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('comum.recusouPedido'));
    } finally {
      setBusy(false);
    }
  };

  const pedirSeparar = async (item: PackItem) => {
    const n = await quantas.pedir({
      defId: item.defId, nome: item.name, max: item.qty - 1, inicial: 1, titulo: t('mochila.separarTitulo')
    });
    if (n) await separar(item, n);
  };

  /** Oferecer na troca ou guardar no banco: a pilha pergunta "Quantas?" antes. */
  const comQuantas = async (item: PackItem, titulo: string, fazer: (qty: number) => void) => {
    const n = await quantas.pedir({ defId: item.defId, nome: item.name, max: item.qty, titulo });
    if (n) fazer(n);
  };

  const arrastarItem = (e: React.PointerEvent<HTMLElement>, item: PackItem, realce: string | null) => {
    prepararArraste(e, { [DRAG_ITEM]: item.id }, {
      onInicio: () => setAlvoDoArraste(realce),
      onFim: () => { setAlvoDoArraste(null); setOverBag(null); }
    });
  };

  const soltarNoSlot = (slot: string, id: string) => {
    setAlvoDoArraste(null);
    if (dead && DEAD_LOCK.has(slot)) return;
    const item = loose.find(i => i.id === id);
    if (!item) return;
    const def = defs.get(item.defId);
    if (!def || wornSlot(def.equipSlot) !== slot) return;
    void equip(item);
  };

  const soltarNaCelula = (index: number, carga: Soltura['carga']) => {
    setOverBag(null);
    const corpse = lerCorpseSlot(comoDataTransfer(carga), DRAG_CORPSE);
    if (corpse && onCorpseDrop) {
      onCorpseDrop(corpse.id, corpse.itemId, corpse.qty);
      return;
    }
    if (onOffer || busy) return;
    const id = carga[DRAG_ITEM];
    const item = pack?.items.find(i => i.id === id);
    if (!item || !id) return;
    if (item.equippedSlot) {
      if (dead && DEAD_LOCK.has(item.equippedSlot)) return;
      void unequip(item, index);
      return;
    }
    // Pilha sobre pilha igual junta; pilha numa célula vazia pergunta "Quantas?" e separa (techspec-pilha-de-pecas §13).
    const ocupante = cells[index];
    if (ocupante && ocupante.id !== item.id && mesmaPilha(item, ocupante)) {
      void juntar(item, ocupante);
      return;
    }
    if (!ocupante && separavel(item)) {
      void quantas.pedir({ defId: item.defId, nome: item.name, max: item.qty, titulo: t('mochila.moverTitulo') }).then(n => {
        if (!n) return;
        if (n >= item.qty) setBagIds(prev => placeAt(prev, id, index));
        else void separar(item, n, index);
      });
      return;
    }
    setBagIds(prev => placeAt(prev, id, index));
  };

  // Um alvo só para a mochila inteira: o slot do boneco, a célula e o vão da grade.
  useAlvoDeArraste(raizRef, {
    sobre: ({ carga, alvo }) => {
      const slotEl = alvo.closest<HTMLElement>('[data-doll-slot]');
      if (slotEl) {
        const slot = slotEl.dataset.dollSlot!;
        return !!carga[DRAG_ITEM] && !(dead && DEAD_LOCK.has(slot));
      }
      const celula = alvo.closest<HTMLElement>('[data-celula]');
      if (celula) {
        if (busy || (onOffer && !onCorpseDrop)) return false;
        // Carga que não é da mochila nem do cadáver (ex.: item do cofre) segue para o alvo de fora.
        if (!carga[DRAG_ITEM] && !lerCorpseSlot(comoDataTransfer(carga), DRAG_CORPSE)) return false;
        const i = Number(celula.dataset.celula);
        if (overBag !== i) setOverBag(i);
        return true;
      }
      if (alvo.closest('.pack-grid')) {
        return !!onCorpseDrop && !busy && !!lerCorpseSlot(comoDataTransfer(carga), DRAG_CORPSE);
      }
      return false;
    },
    solta: ({ carga, alvo }) => {
      const slotEl = alvo.closest<HTMLElement>('[data-doll-slot]');
      if (slotEl) {
        const id = carga[DRAG_ITEM];
        if (id) soltarNoSlot(slotEl.dataset.dollSlot!, id);
        return;
      }
      const celula = alvo.closest<HTMLElement>('[data-celula]');
      if (celula) {
        soltarNaCelula(Number(celula.dataset.celula), carga);
        return;
      }
      const corpse = lerCorpseSlot(comoDataTransfer(carga), DRAG_CORPSE);
      if (corpse && onCorpseDrop) {
        setOverBag(null);
        onCorpseDrop(corpse.id, corpse.itemId, corpse.qty);
      }
    },
    sai: () => setOverBag(null)
  });

  const sel = loose.find(i => i.id === selected);
  const defSel = sel ? defs.get(sel.defId) : undefined;
  const slotSel = defSel && wornSlot(defSel.equipSlot) !== null && !defSel.stackable
    ? wornSlot(defSel.equipSlot)
    : null;
  const travadoSel = dead && !!slotSel && DEAD_LOCK.has(slotSel);
  const corTintaSel = sel ? corDaTinta(sel.defId) : null;
  const skinSel = sel ? skinDoItem(sel.defId) : null;

  let areaDaFicha: ReactNode;
  if (error && !dropAlvo) {
    areaDaFicha = <p className="inv-ficha notice" role="alert">{error}</p>;
  } else if (onOffer) {
    areaDaFicha = (
      <div className="inv-ficha inv-ficha--vazia">
        <p>{offeredIds.length === 1 ? t('mochila.fichaTrocaUm') : t('mochila.fichaTroca', { n: offeredIds.length })}</p>
      </div>
    );
  } else if (onBankDeposit) {
    areaDaFicha = (
      <div className="inv-ficha inv-ficha--vazia">
        <p>{t('mochila.fichaBanco')}</p>
      </div>
    );
  } else if (!pack) {
    areaDaFicha = (
      <div className="inv-ficha inv-ficha--vazia">
        <p>{t('mochila.abrindo')}</p>
      </div>
    );
  } else if (sel) {
    areaDaFicha = (
      <FichaDoItem
        item={sel}
        slot={slotSel}
        travado={travadoSel}
        busy={busy}
        onVestir={() => { if (slotSel) void equip(sel, slotSel); }}
        onJogar={() => pedirDrop(sel)}
        onSeparar={separavel(sel) ? () => void pedirSeparar(sel) : undefined}
        tinta={corTintaSel ? {
          guardada: traje ? traje.guardadas.includes(corTintaSel) : null,
          raridade: traje?.cores.find(c => c.id === corTintaSel)?.raridade,
          onGuardar: () => void guardarTinta(sel)
        } : skinSel ? {
          guardada: traje ? !!traje.exclusivas?.some(x => x.id === skinSel) : null,
          skin: true,
          onGuardar: () => void guardarTinta(sel)
        } : undefined}
      />
    );
  } else {
    areaDaFicha = (
      <div className="inv-ficha inv-ficha--vazia">
        <p className="so-mouse"><b>{t('mochila.fichaVaziaAcao')}</b> {t('mochila.fichaVazia')}</p>
        <p className="so-toque"><b>{t('mochila.fichaVaziaToqueAcao')}</b> {t('mochila.fichaVaziaToque')}</p>
      </div>
    );
  }

  const rodape = pack && (() => {
    const faixa = faixaDoPeso(pack.weight, pack.weightMax);
    const txt = t('mochila.pesoValor', { a: stones(pack.weight), b: stones(pack.weightMax) });
    return (
      <div className="pack-foot">
        <div className={`peso is-${faixa}`} title={t('comum.peso')}>
          <span className="peso__rot" aria-hidden="true">{t('comum.peso')}</span>
          <span className="peso__trilho" role="meter" aria-label={t('comum.peso')}
            aria-valuemin={0} aria-valuemax={pack.weightMax}
            aria-valuenow={Math.min(pack.weight, pack.weightMax)} aria-valuetext={txt}>
            <span className="peso__cheio" style={{ width: `${fracao(pack.weight, pack.weightMax)}%` }} />
          </span>
          <span className="peso__num" aria-hidden="true"><b>{stones(pack.weight)}</b> / {stones(pack.weightMax)} {t('comum.stones')}</span>
        </div>
        <span className="pack-coins">
          <span className="pack-silver moeda" title={t('mochila.prata')}>
            <img src={iconeDaPrata()} alt="" width={16} height={16} draggable={false} />
            {formatGold(pack.silver ?? 0)}
          </span>
          <span className="pack-gold moeda" title={t('item.gold')}>
            <img src={iconeDoOuro()} alt="" width={16} height={16} draggable={false} />
            {formatGold(pack.gold)}
          </span>
        </span>
        {onVender && (
          <button type="button" className="btn btn--tiny vd-btn" onClick={onVender} disabled={dead}>
            {t('mochila.vender')}
          </button>
        )}
      </div>
    );
  })();

  const modal = dropAlvo && (
    <div className="modal" role="presentation" onClick={() => { if (!busy) fecharModal(); }}>
      <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="drop-title" onClick={e => e.stopPropagation()}>
        <Gump
          title={t('mochila.dropTitulo')}
          action={
            <button type="button" className="btn btn--tiny btn--ghost" onClick={() => fecharModal()} disabled={busy} aria-label={t('comum.fechar')}>
              ×
            </button>
          }
        >
          <div className="stack">
            <p id="drop-title" className="modal__item">
              <ItemIcon id={dropAlvo.defId} size={ICON} />
              <span>
                {dropAlvo.qty > 1
                  ? t('mochila.dropAvisoQtd', { item: nomeDoItem(dropAlvo.defId, dropAlvo.name), qtd: numero(dropAlvo.qty) })
                  : t('mochila.dropAviso', { item: nomeDoItem(dropAlvo.defId, dropAlvo.name) })}
              </span>
            </p>
            {error && <p className="notice" role="alert">{error}</p>}
            <div className="row row--split">
              <button type="button" className="btn btn--ghost" data-foco-inicial onClick={() => fecharModal()} disabled={busy}>{t('comum.cancelar')}</button>
              <button type="button" className="btn btn--danger" onClick={() => void confirmarDrop()} disabled={busy}>
                {t('mochila.dropConfirmar')}
              </button>
            </div>
          </div>
        </Gump>
      </div>
    </div>
  );

  return (
    <>
      <div ref={raizRef} className={`pack inv${alvoDoArraste ? ' is-arrastando' : ''}`}>
        <section className="inv-equip">
          <div className="inv__sec-h"><h3 className="inv__rotulo">{t('mochila.equipamento')}</h3></div>
          <div className="doll" role="group" aria-label={t('mochila.equipamento')}>
            <div className="doll__fig">{boneco}</div>
            <span className="doll__chao" aria-hidden="true" />
            <span className="doll__linha" aria-hidden="true" />
            {ORDEM_VISUAL.map(id => {
              const slot = SLOT_POR_ID.get(id)!;
              const item = bySlot.get(slot.id);
              const locked = dead && !!slot.lockWhenDead;
              const rotulo = nomeDoSlot(slot.chave);
              return (
                <button
                  key={slot.id}
                  type="button"
                  ref={el => { if (el) slotRefs.current.set(slot.id, el); else slotRefs.current.delete(slot.id); }}
                  className={`doll-slot ${slot.cls} ${item ? 'is-worn' : 'is-empty'}${locked ? ' is-locked' : ''}${alvoDoArraste === slot.id && !locked ? ' is-alvo' : ''}`}
                  aria-label={
                    locked
                      ? t('mochila.travado', { slot: rotulo })
                      : item
                        ? t('mochila.slotComItem', { slot: rotulo, item: nomeDoItem(item.defId, item.name) })
                        : rotulo
                  }
                  title={locked ? t('mochila.travadoMorto') : item ? itemHint(item) : rotulo}
                  disabled={busy || locked}
                  data-doll-slot={slot.id}
                  onPointerDown={e => { if (item && !locked && !busy && !onOffer) arrastarItem(e, item, null); }}
                  onClick={() => {
                    if (!item || locked) return;
                    void unequip(item);
                  }}
                >
                  {item
                    ? <ItemIcon id={item.defId} label={nomeDoItem(item.defId, item.name)} size={ICON} eager />
                    : <ItemIcon id={slot.ghost} label="" size={ICON} />}
                </button>
              );
            })}
          </div>
          {onGuardaRoupa && (
            <button type="button" className="inv__gr" aria-haspopup="dialog" disabled={dead} onClick={onGuardaRoupa}>
              <IcGuardaRoupa />{t('traje.titulo')}
              {pack?.cloth?.includes('t:') && <small>{t('traje.ligado')}</small>}
            </button>
          )}
        </section>

        <section className="inv-pack">
          <div className="inv__sec-h">
            <h3 className="inv__rotulo">{t('mochila.titulo')}</h3>
            {onOffer ? <span className="inv__dica">{t('troca.cliqueParaOferecer')}</span>
              : onBankDeposit ? <span className="inv__dica">{t('mochila.cliqueDeposita')}</span>
              : pack ? <span className="inv__conta">{loose.length === 1 ? t('mochila.itensUm') : t('mochila.itens', { n: loose.length })}</span>
              : null}
          </div>
          <div className="pack-grid">
            {cells.map((item, i) => {
              const def = item ? defs.get(item.defId) : undefined;
              const wearable = def ? wornSlot(def.equipSlot) !== null && !def.stackable : false;
              const offered = item ? offeredIds.includes(item.id) : false;
              return (
                <div
                  key={item ? item.id : `empty-${i}`}
                  className={`pack-cell${item && selected === item.id ? ' is-on' : ''}${item?.qualidade === 2 ? ' is-exc' : ''}${item && corDaTinta(item.defId) ? ' is-prime' : ''}${item ? '' : ' is-empty'}${offered ? ' is-oferecido' : ''}${!pack && !error ? ' is-skel' : ''}${overBag === i ? ' is-over' : ''}`}
                  title={item ? (onOffer ? t('troca.dicaOferecer', { dica: itemHint(item) }) : itemHint(item)) : t('mochila.titulo')}
                  data-celula={i}
                  style={item && corDaTinta(item.defId)
                    ? { '--gc': COR_DA_RARIDADE[traje?.cores.find(c => c.id === corDaTinta(item.defId))?.raridade ?? ''] ?? 'var(--gold-lit)' } as CSSProperties
                    : undefined}
                  onPointerDown={e => {
                    if (!item || busy || onOffer) return;
                    arrastarItem(e, item, wornSlot(defs.get(item.defId)?.equipSlot ?? ''));
                  }}
                >
                  {item
                    ? (
                      <button
                        type="button"
                        className="pack-cell__hit"
                        ref={el => { if (el) hitRefs.current.set(i, el); else hitRefs.current.delete(i); }}
                        disabled={busy}
                        aria-pressed={onOffer ? offered : selected === item.id}
                        onClick={() => {
                          if (onOffer) {
                            // Tudo se troca e aposta, abençoado também (Renato, 16/10 e 28/10/2026).
                            const oferecer = onOffer;
                            void comQuantas(item, t('quantas.oferecer'), n => oferecer(item, n));
                          }
                          else if (onBankDeposit) {
                            const guardar = onBankDeposit;
                            void comQuantas(item, t('quantas.guardarBanco'), n => guardar(item, n));
                          }
                          else { setError(null); setSelected(item.id); }
                        }}
                        onDoubleClick={() => { if (!onOffer && wearable) void equip(item); }}
                      >
                        <ItemIcon id={item.defId} label={nomeDoItem(item.defId, item.name)} size={ICON} quantity={item.qty} eager />
                      </button>
                    )
                    : null}
                </div>
              );
            })}
          </div>
        </section>

        {areaDaFicha}
        {pack && rodape}
      </div>
      {dropAlvo && createPortal(modal, document.body)}
      {quantas.dialogo}
    </>
  );
}
